import _ from 'lodash';

import Component from '../classes/component';
import QuantitySelector from './quantity-selector';
import StatefulButton from './stateful-button';
import { extractContent, formToArray, formToObject } from '../utils/dom-utils';
import Price from './price';
import { Variation } from '../types/store-types';
import { addToCart, AddToCartPayload, getVariation, GetVariationPayload } from '../api/store-api';
import App from '../classes/app';
import CartCount from './cart-count';
import Modal from './modal';

const SELECTORS = {
  form: '#product-customizer-form',
  input: 'input',
  submit: 'button[type="submit"]',
  errorMessage: '.error-message',
  attributeSelector: '.attribute-selector',
  productImg: '.product-img',
  nutritionalValuesImg: '.nutritional-values-img',
  nutritionalValuesBtn: '.nutritional-values-btn',
  flavorNameLabel: '#flavor-name',
  checkedFlavor: 'input[name="pa_saveur"]:checked',
};

class ProductCustomizer extends Component<typeof SELECTORS, HTMLFormElement> {
  protected _selectors = SELECTORS;

  private _quantitySelector: QuantitySelector;

  private _price: Price;

  private _submitButton: StatefulButton;

  private _variation?: Variation | null;

  private get _state() {
    const attributes = formToArray(this._getChild('form'));
    const quantity = this._quantitySelector.value;

    return { attributes, quantity };
  }

  private get _hasNewAttributes() {
    if (!this._variation) return true;

    const attributes = formToObject(this._getChild('form'));
    return Object.keys(this._variation.attributes).some(
      attribute =>
        this._variation?.attributes[attribute] !== attributes[attribute.replace('attribute_', '')],
    );
  }

  private get _formData(): GetVariationPayload {
    const data = { product_id: this._productId } as GetVariationPayload;
    this._state.attributes.forEach(({ name, value }) => {
      data[`attribute_${name}`] = value;
    });

    return data;
  }

  private get _submitData(): AddToCartPayload | null {
    if (!this._variation) return null;

    return {
      product_id: this._variation.variation_id,
      quantity: this._quantitySelector.value,
    };
  }

  private get _productId() {
    return Number(this._getChild('form').dataset.productId);
  }

  private get _productImageId() {
    return Number(this._getChild('form').dataset.imageId);
  }

  private get _canAddToCart() {
    return Boolean(this._variation?.is_purchasable && this._variation?.is_in_stock);
  }

  private _updateUrlParams() {
    const updatedParams = new URLSearchParams();
    this._state.attributes.forEach(({ name, value }) => {
      updatedParams.append(name.replace('pa_', ''), value);
    });
    const { origin, pathname } = window.location;
    const updatedUrl = `${origin}${pathname}?${updatedParams.toString()}`;
    window.history.replaceState({ url: updatedUrl }, '', updatedUrl);
  }

  private _enableInputs() {
    this._getChildren<HTMLInputElement>('input').forEach(input => {
      input.parentElement?.classList.remove('disabled');
    });
  }

  private _disableInputs() {
    this._getChildren<HTMLInputElement>('input').forEach(input => {
      input.parentElement?.classList.add('disabled');
    });
  }

  private async _handleSubmit() {
    if (
      this._submitButton.isLoading ||
      this._submitButton.isDisabled ||
      !this._submitData ||
      !this._canAddToCart
    ) {
      return;
    }

    this._disableInputs();
    this._submitButton.isLoading = true;
    this._submitButton.disable();

    const res = await addToCart(this._submitData);

    if (res.success) {
      this._getChild('errorMessage').style.display = 'none';

      this._submitButton.isLoading = false;
      this._submitButton.showFeedback('Ajouté!', 'success', 2000);
      App.getComponentsOfType<CartCount>('cart-count').forEach(cartCount => {
        cartCount.updateCount();
      });
    } else {
      this._getChild('errorMessage').style.display = 'flex';
    }

    this._enableInputs();
    this._submitButton.isLoading = false;
    this._submitButton.enableIf(this._canAddToCart);
  }

  private async _handleStateChange(eventOrQuantity?: Event | number) {
    if (this._submitButton.isLoading) return;

    this._disableInputs();
    this._submitButton.isLoading = true;
    this._submitButton.disable();

    const isAttributeUpdate = typeof eventOrQuantity !== 'number';

    if (isAttributeUpdate) {
      if (this._hasNewAttributes) {
        const res = await getVariation(this._formData);
        if (res.success) {
          this._updateUrlParams();

          if (res.data.error) {
            this._submitButton.showFeedback(res.data.error, 'error');
            this._variation = null;

            const noImage = document.createElement('div');
            noImage.className = 'product-img';
            this._getChild<HTMLImageElement>('productImg').replaceWith(noImage);

            this._getChildComponent<Modal>('modal').close();
            this._getChild('nutritionalValuesBtn').classList.remove('show');

            this._quantitySelector.maxQuantity = 0;
            this._quantitySelector.minQuantity = 0;

            const checkedFlavor = this._getChild<HTMLInputElement>('checkedFlavor');
            this._getChild('flavorNameLabel').innerHTML =
              checkedFlavor?.dataset.flavorName ?? 'Inconnu';
          } else {
            this._variation = res.data as Variation;

            const newImage = document.createElement('img');
            newImage.className = 'product-img';
            newImage.src = this._variation.image;
            this._getChild<HTMLImageElement>('productImg').replaceWith(newImage);

            if (this._variation.nutritional_values) {
              this._getChild('nutritionalValuesBtn').classList.add('show');
              this._getChild<HTMLImageElement>('nutritionalValuesImg').src =
                this._variation.nutritional_values;
            } else {
              this._getChildComponent<Modal>('modal').close();
              this._getChild('nutritionalValuesBtn').classList.remove('show');
            }

            this._quantitySelector.maxQuantity = this._variation.max_qty;
            this._quantitySelector.minQuantity = this._variation.min_qty;

            this._getChild('flavorNameLabel').innerHTML = this._variation.flavor_name;

            const { availability_html } = this._variation;
            if (availability_html) {
              const feedback = extractContent(availability_html);
              if (feedback) this._submitButton.showFeedback(feedback, 'warning');
            } else {
              this._submitButton.hideFeedback();
            }
          }
        }
      }
    }

    if (this._variation) {
      const { display_price, display_regular_price } = this._variation;
      const quantity = isAttributeUpdate ? this._quantitySelector.value : eventOrQuantity;
      const price = display_regular_price * quantity;
      const isOnSale = display_price < display_regular_price;
      const discountedPrice = isOnSale ? display_price * quantity : undefined;
      this._price.setValue(price, discountedPrice);
    }

    this._enableInputs();
    this._submitButton.isLoading = false;
    this._submitButton.enableIf(this._canAddToCart);
  }

  private _initializeSelections() {
    const urlParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlParams.entries());

    this._getChildren<HTMLInputElement>('attributeSelector').forEach(selector => {
      const { taxonomy } = selector.dataset;
      const attribute = taxonomy?.replace('pa_', '') ?? '';
      const inputSelector = params[attribute] ? `input[value="${params[attribute]}"]` : 'input';
      const input = selector.querySelector<HTMLInputElement>(inputSelector);
      if (input) input.checked = true;
    });

    this._handleStateChange();
  }

  protected async _onEnter() {
    this._quantitySelector = this._getChildComponent<QuantitySelector>('quantity-selector');
    this._price = this._getChildComponent<Price>('price');
    this._submitButton = this._getChildComponent<StatefulButton>('stateful-button');

    this._quantitySelector.addChangeListener(this._handleStateChange.bind(this));
    this._addListener('input', 'change', this._submitButton.disable);
    this._addListener('input', 'change', this._handleStateChange, 800);
    this._addListener('form', 'submit', this._handleSubmit);

    this._initializeSelections();
  }
}

export default ProductCustomizer;
